import { SxProps, Theme } from '@mui/material';
import { ApplicationContext, ClientTool } from '@opengov/agents-browser-sdk';
import { LoggerConfig } from '@opengov/psc-react-logger';

export interface IEntityDetails {
  id: string;
  name: string;
  displayName?: string;
  friendlyId: string;
  entityType: string;
  entitledApplications: IProductDetails[];
  federatedServiceIds?: FederatedServiceIds;
}

export interface IGraphQlResponseEntity {
  id: string;
  entityId: number;
  name: string;
  displayName: string;
  friendlyId: string;
  entityType: string;
  federatedServiceIds?: FederatedServiceIds;
  entityAppEntitlements?: {
    entitlements: IProductDetails[];
  };
}

export interface IContextSwitcherData {
  entitledEntities: IEntityDetails[];
  activeEntity: IEntityDetails;
  activeProductKey: PRODUCT_KEY;
  forcedDefaultApplication?: PRODUCT_KEY;
  forceContextSwitcherVisibility?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingError?: boolean;
  onEntitySelect?: OnEntitySelectCallback;
}

/**
 * Called when a user selects an entity in the context switcher. Return `false`
 * (sync or resolved from a promise) to suppress the switcher's default full-page
 * navigation because the consumer handled the switch itself (e.g. an in-app
 * client-side switch). Any other result — `void`, `true`, or a throw/rejection —
 * falls back to the default full-page navigation. Promises are awaited before
 * deciding whether to navigate; when a promise is returned the menu closes
 * immediately (the async switch is assumed to be handled in-app), while sync
 * results close the menu only when they return `false`.
 */
export type OnEntitySelectCallback = (entity: IEntityDetails) => boolean | void | Promise<boolean | void>;

export interface IFavoritesSection {
  id: string;
  label?: string;
  items: IMenuItem[];
}
export interface ISubmenuSection {
  title?: string;
  items?: IMenuItem[];
}

export type IMenuItem = {
  id: string;
  label: string;
  shouldFetchNestedMenuItems?: boolean;
  submenuSections?: ISubmenuSection[];
  openInNewTab?: boolean;
  url?: string;
  linkComponent?: any;
  onClick?(): void;
};

export interface IMenuOption {
  id: string;
  /**
   * The text to display when hovering over the menu item.
   * If not provided, a tooltip will only be displayed with the label text
   * if the label text is truncated.
   */
  tooltipText?: string;
  label: string;
  submenuSections?: ISubmenuSection[];
  /**
   * This is an optional prop to set the max width of the top level menu items in Desktop view.
   * Do NOT set this to leverage all screen real estate.
   */
  maxWidth?: string;
  /**
   * Indicates that the next level of menu items should be fetched asynchronously.
   * Invokes the `getSubmenuItems` function to fetch the nested menu items.
   */
  shouldFetchNestedMenuItems?: boolean;
  /**
   * If provided, `counterAdornmentValue` will be displayed as a badge next to the label menu item.
   */
  counterAdornmentValue?: number;
  openInNewTab?: boolean;
  /**
   * Indicates that the menu item should be styled as active.
   */
  isActive?: boolean;
  /**
   *
   * Supports a custom React component to be rendered in place of the default anchor tag
   *
   *```typescript
   // EXAMPLE
   * const CustomLinkComponent = React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps & { href: string }, "to">>((props, ref) => {
   * return (
   *   <RouterLink
   *     ref={ref}
   *     to={props.href}
   *     {...props}
   *   />)
   * )};
   * ```
   *
   * @see {@link https://mui.com/material-ui/react-link/#third-party-routing-library}
   * @see {@link https://mui.com/material-ui/integrations/routing/#link}
   */
  linkComponent?: any;
  /**
   * If a `url` is provided, the menu item will be rendered as an anchor tag,
   * or a custom link component if `linkComponent` is provided.
   */
  url?: string;
  /**
   * If an `onClick` function is provided, the menu item will be rendered as a button.
   * It is assumed that `url` and `onClick` are mutually exclusive.
   */
  onClick?(): void;
}

export interface IUpdatedIdOrder {
  id: string;
  updatedIdOrder: string[];
}

export interface ISearchOptions {
  handleSearchIconClick(): void;
}
export interface IUtilityTrayOptions {
  searchOptions?: ISearchOptions;
  createNewActionOptions?: ICreateNewActionItem[];
  profileSettingsOptions: IProfileSettingsOptions;
  settingsOptions?: UtilityOptions;
  helpOptions?: UtilityOptions;
  reportsOptions?: UtilityOptions;
  notificationsOptions?: UtilityOptions;
  customUtilityOptions?: CustomUtilityOptions;
  /**
   * Custom React node to render in place of the default profile icon / menu.
   * When provided, `profileSettingsOptions` is ignored and no avatar or profile
   * dropdown is rendered. Use for unauthenticated views or contexts where a
   * custom action (e.g. a sign-in button) belongs in the profile slot.
   *
   * Note: We used `any` instead of `React.ReactNode` to avoid React as a
   * dependency, which breaks bundled packages that lack React.
   */
  customProfileSlot?: any;
}

export interface ICreateNewActionItem {
  label: string;
  dataOg?: string;
  handleCreate(): void;
}

export interface IFavoritesWithContext {
  menuOptions: IMenuOption[];
  favorites?: IFavoritesSection[];
  emptyFavoritesHeader?: string;
  emptyFavoritesMessage?: string;
  autoDismissToasts?: boolean;
  toastAnchorElement?: HTMLElement;
  fullPageWidth?: boolean;
  onFavoriteReorder?(IUpdatedIdOrder): Promise<string[]>;
  onFavoriteSaveClick?(menuLink: IMenuItem): Promise<IMenuItem>;
  onFavoriteDeleteClick?(menuLink: IMenuItem): Promise<IMenuItem>;
}

/**
 *
 * This interface defines the configuration options for Auth0 integration.
 * It is used to specify the necessary parameters for setting up Auth0 authentication.
 *
 *  When using multiple Auth0Providers in a single application you should do the following to ensure sessions are not overwritten:
 *  - Configure a different redirect_uri for each Auth0Provider, and set skipRedirectCallback for each provider to ignore the others redirect_uri
 *  - If using localstorage for both Auth0Providers, ensure that the audience and scope are different for so that the key used to store data is different
 *
 */

export type Auth0Options = {
  /**
   * The Client ID found on your Application settings page.
   * This is a unique identifier for your Auth0 application.
   */
  auth0clientID: string;

  /**
   * The login domain. This is required for Auth0 setup and is typically in the format of `your-domain.auth0.com`
   */
  auth0domain: string;

  /**
   * The default `redirect_uri` where Auth0 will redirect your browser to with the authentication result.
   * It must be whitelisted in the "Allowed Callback URLs" field in your Auth0 Application's settings.
   */
  auth0callbackUrl: string;

  /**
   * Configure `skipRedirectCallback` for each provider to ignore the others' redirect_uri.
   *
   * By default, if the page URL has code/state params, the SDK will treat them as Auth0's
   * and attempt to exchange the code for a token. In some cases, the code might be for
   * something else (another OAuth SDK perhaps). In these instances, you can instruct the
   * client to ignore them.
   *
   * Example: If you have multiple Auth0Providers in your app, you can set skipRedirectCallback
   * to true for all but one provider.
   *
   * ```jsx
   * <Auth0Provider
   *   clientId={clientId}
   *   domain={domain}
   *   skipRedirectCallback={window.location.pathname === '/stripe-oauth-callback'}
   * >
   * ```
   */
  auth0skipRedirectCallback?: boolean;
  /**
   * The audience parameter is the identifier of the API that the client application wants to access.
   * This is not required for all Auth0 applications, as Auth0 calls default to 'opengov' as the audience,
   * but you can specify a different audience if needed.
   */
  auth0audience?: string;
  /**
   * Resolves a currently-valid access token, called before every OG Assist request. Pass this
   * alongside `auth0token` when your application owns the Auth0 session — a host-provided
   * `auth0token` is a snapshot the nav bar does not renew, so it eventually expires.
   * `getAccessTokenSilently` from `@auth0/auth0-react` satisfies this directly. Unnecessary when the
   * nav bar owns the Auth0 configuration.
   */
  auth0GetAccessToken?(): Promise<string>;
  /**
   * Called when Auth0 session expires and user needs to re-authenticate.
   * The parent application is responsible for handling the authentication flow.
   * Invoked only when fetching an access token fails with `login_required`.
   *
   * **Strongly recommended:** Pass this prop to ensure proper session synchronization with Auth0.
   * When the session cannot be refreshed, the app should typically log the user out, as authentication
   * is required for nav bar features like context switcher and OG Assist to function properly.
   * Commonly set to the same callback used for handleSignOut.
   */
  onAuth0SessionExpired?(): void;
  /**
   * This function is called when the Auth0 SDK redirects back to the application.
   *
   */
  onRedirectCallback?(appState?: any): void;
};

export type IAuth0Config =
  | ({
      /**
       * If an access token is provided, the remaining auth0 configuration is not required.
       * The provided access token will be used instead as the bearer token for API requests requiring authentication
       */
      auth0token: string;
    } & Partial<Auth0Options>)
  | ({
      auth0token?: string;
    } & Auth0Options);

export interface IOGAgentOptions {
  /**
   * Entity UUID is the preferred way to identify an entity. If this is provided, platformBaseUrl is not needed.
   */
  entityUUID?: string;
  /**
   * Friendly ID is the alternate way to identify an entity. friendlyId's are equivalent to an entity subdomain. If this is provided, platformBaseUrl is required.
   */
  friendlyId?: string;
  /**
   * The base URL for the agents. This is required.
   */
  agentsBaseUrl: string;
  /**
   * The base URL for the platform. This is only needed if friendlyId is provided.
   */
  platformBaseUrl?: string;
  auth0Config: IAuth0Config;
  /**
   * The ref object to the DOM element that will be used as the portal target for the drawer.
   * When not provided, the drawer will still appear, but it will cover up the content of the page underneath it rather than pushing content aside.
   * Note: We used `any` instead of `React.RefObject<HTMLElement>` to avoid React as a dependency, which breaks bundled packages that lack React.
   */
  drawerPortalTargetRef?: any;
  /**
   * A callback function that is called when the OG Assist menu is opened or closed.
   */
  onDrawerToggle?(open: boolean): void;
  /**
   * Application context that provides structured information about the current user environment
   * (e.g., current page, active document, user role, permissions).
   * This context is sent with each message to help the AI agent provide more relevant responses.
   *
   * @see {@link ApplicationContext} for the schema definition
   */
  applicationContext?: ApplicationContext;
  /**
   * Client tools configuration that enables the AI agent to interact with the host application.
   * Provide a `toolRegistry` (array of tool definitions and their handler functions).
   * Tools execute client-side, giving the agent access to page state and browser APIs for context-aware
   * interactions.
   *
   * @see {@link ClientTools} for the full interface definition
   */
  clientTools?: ClientTool[];
  /**
   * Controls whether the OG Assist button should be displayed in the utility tray.
   * When true, the button will be shown. When false, it will be hidden.
   * When undefined, defaults to true if unstable_ogAgentOptions is provided and not empty.
   */
  showOgAssist?: boolean;
  /**
   * When true, requests that the OG Assist drawer start open in the current context (e.g. on a specific page).
   * When false or undefined, the drawer starts closed. The host app can set this to true when the user navigates to
   * areas where OG Assist should be open by default (e.g. requestDrawerOpen={isOnSomeSpecificPage}), and set it
   * to false when the user leaves. The user can still toggle the drawer open or closed at any time; this prop only
   * sets the initial/requested state when the host changes it (e.g. on route change).
   */
  requestDrawerOpen?: boolean;
  /**
   * Overrides the heading shown in the OG Assist welcome message. When omitted, OG Assist uses its own
   * personalized default (`Hello {first name}! Where should we begin?`); pass an empty string to render no heading.
   */
  welcomeMessage?: string;
  /**
   * Overrides the suggestion prompts shown in the OG Assist welcome message. When omitted, OG Assist uses its own
   * defaults; pass an empty array to render no suggestions.
   *
   * Emptying both this and `welcomeMessage` leaves the drawer with just its composer.
   */
  welcomeSuggestions?: string[];
}

export interface IContextSwitcherOptions {
  // Required to fetch the list of entitled entities & applications specific to the user
  userUUID: string;
  // Specifies the base domain that will prefix the call to GraphQL
  // to fetch the list of entitled entities & applications
  baseUrl: string;
  /**
   * The entity currently being viewed, also referred to as the "Federated Service ID".
   * This value must match the entity's information as returned by the access control API response.
   */
  activeEntityId: string;
  /**
   * The type of entity currently being viewed.
   * This value is used to match against the entity type in the GraphQL query:
   *
   * ```typescript
   * accessibleEntities: [
   *   {
   *     id: "ENTITY_UUID",
   *     entityId: 1,
   *     displayName: "Example Entity",
   *     name: "example_entity",
   *     friendlyId: "friendly_example_entity", //BY_FRIENDLY_ID
   *     federatedServiceIds: {
   *       citCommunityId: "cid_example_entity", // BY_CIT_COMMUNITY_ID
   *       financialsCustomerId: "fi_example_entity", // BY_FINANCIALS_CUSTOMER_ID
   *       othPortalId: "oth_example_entity", // BY_OTH_PORTAL_ID
   *       ckanCustomerId: "ckan_example_entity", // BY_CKAN_CUSTOMER_ID
   *       procurementId: "pro_example_entity", // BY_PROCUREMENT_ID
   *       assetManagementId: "eam_example_entity", // BY_ASSET_MANAGEMENT_ID
   *     },
   *     entityAppEntitlements: {
   *       entitlements: [],
   *       entityUuid: "ENTITY_UUID",",
   *     },
   *   },
   * ]
   * ```
   */
  activeEntityIdType: FEDERATED_SERVICE_ID_TYPE;
  // The application currently being viewed
  activeProductKey?: PRODUCT_KEY;
  // Optional configuration for Auth0
  // If not provided, cookies will be used to authenticate the user
  auth0Config?: IAuth0Config;
  /**
   * The application that the user should always be navigated to upon clicking a new entity.
   *
   * When this prop is set:
   * - The application switcher will be hidden.
   * - Only entities that support this application will be shown.
   *
   * Note: Default application logic does not apply when applications are hidden.
   * Clicking an entity navigates to the default product specified in this prop.
   */
  forcedDefaultApplication?: PRODUCT_KEY;
  /**
   * Force the context switcher to be interactive, regardless of other visibility conditions.
   * Enable this when you want the context switcher UI dropdown to be visible, even in scenarios where it
   * would normally be hidden (e.g. when there is only one entity and one non-admin application and you are on
   * the My Profile page).
   */
  forceContextSwitcherVisibility?: boolean;
  /**
   * When true, the context switcher trigger renders as a non-interactive static label,
   * even if entitled entities/apps would normally enable it. Use for views where
   * switching context is inappropriate (e.g. signed-out, onboarding, kiosk modes).
   */
  disabled?: boolean;
  /**
   * Optional handler for entity selection. Return `false` to suppress the default
   * full-page navigation because the switch was handled in-app; any other result
   * (or a throw) falls back to the default navigation. See {@link OnEntitySelectCallback}.
   */
  onEntitySelect?: OnEntitySelectCallback;
}
export interface ISwitchMenuItem {
  title: string;
  description: string;
  // Defaults to 'On'
  switchLabel?: string;
  value: boolean;
  dataOG?: string;
  /**
   * Indicates whether this switch menu item requires OG Assist permission to be visible.
   * When true, the item will be conditionally rendered based on the same permission check as the OG Assist button.
   */
  requiresOgAssistPermission?: boolean;
  onValueChange(value: boolean): Promise<void>;
}

export interface ISwitchMenuOptions {
  menuButtonLabel: string;
  menuItems: ISwitchMenuItem[];
}
export interface INavBarOptions {
  appName: string;
  /**
   * When true, the OpenGov logo is not rendered in the nav bar header.
   * Use for white-labeled or partner-branded contexts.
   */
  hideOpenGovLogo?: boolean;
  openGovLogoUrl?: string;
  openGovLogoOnClick?: () => void;
  openGovLogoLinkComponent?: any;
  menuOptions: IMenuOption[];
  getSubmenuItems?(menuItem: IMenuOption | IMenuItem): Promise<ISubmenuSection[]>;
  contextSwitcherOptions?: IContextSwitcherOptions;
  customHeaderErrorMessage?: string;
  switchMenuOptions?: ISwitchMenuOptions;
  utilityTrayOptions?: IUtilityTrayOptions;
  showOfflineIndicator?: boolean;
  favorites?: IFavoritesSection[];
  emptyFavoritesHeader?: string;
  emptyFavoritesMessage?: string;
  autoDismissToasts?: boolean;
  toastAnchorElement?: HTMLElement;
  onFavoriteReorder?(updatedIdOrder: IUpdatedIdOrder): Promise<string[]>;
  onFavoriteSaveClick?(menuItem: IMenuItem): Promise<IMenuItem>;
  onFavoriteDeleteClick?(menuItem: IMenuItem): Promise<IMenuItem>;
  loggerOutput?: LoggerOutput;
  unstable_ogAgentOptions?: IOGAgentOptions;
}

type LoggerOutput = LoggerConfig['loggerOutput'];

export interface IMuiStyleObject {
  [key: string]: SxProps<Theme>;
}

export type MuiSxProps = SxProps<Theme>;

declare module '@mui/material/Button' {
  // tslint:disable-next-line:interface-name
  interface ButtonPropsSizeOverrides {
    extraSmall: true;
  }
}

declare module '@mui/material/Typography' {
  // tslint:disable-next-line:interface-name
  interface TypographyPropsVariantOverrides {
    bodyLarge: true;
    bodyDefault: true;
    bodySmall: true;
    bodyXSmall: true;
  }
}

export type UtilityTrayMenuItem = {
  label: string;
  dataOg?: string;
  description?: string;
  openInNewTab?: boolean;
  url?: string;
  linkComponent?: any;
  onClick?(): void;
};

export type UtilityTrayMenuSection = {
  label?: string;
  menuItems: UtilityTrayMenuItem[];
};

export type BadgeVariant = 'dot' | 'standard';

export type UtilityOptions = {
  menuSections: UtilityTrayMenuSection[];
  openInNewTab?: boolean;
  badgeContent?: number;
  badgeVariant?: BadgeVariant;
  /**
   * If provided, the icon will be rendered as an anchor tag,
   * or a custom link component if `iconLinkComponent` is provided.
   */
  iconUrl?: string;
  iconLinkComponent?: any;
  /**
   *
   * @param event The event object
   */
  iconOnClickOverride?(event: any): void;
};

export type CustomUtilityOptions = UtilityOptions & {
  muiIcon?: any;
  stringIcon?: string;
  utilityName: string;
};

export enum UtilityNames {
  reports = 'Reports',
  help = 'Help',
  notifications = 'Notifications',
  settings = 'Settings',
  profile = 'Profile',
  search = 'Search',
  custom = 'Custom'
}

export interface IProfileSettingsOptions {
  placeHolderInitials?: string;
  updateProfileUrl?: string;
  updateProfileLinkComponent?: any;
  profileImgUrl?: string;
  updateProfileOnClick?(): void;
  handleSignOut?(): void;
}
export interface IProductDetails {
  entitlementProductKey: PRODUCT_KEY | MUI_PRODUCT_ICONS;
  displayName?: string;
  url: string;
  description: string;
  openInNewTab: boolean;
  defaultApp?: boolean | null;
}

export enum MUI_PRODUCT_ICONS {
  adminsettings = 'adminsettings'
}

export enum PRODUCT_ICONS {
  // Financials
  financial = 'product-fin',
  // Procurement
  procurement = 'product-pro',
  // Budgeting & Planning
  budget = 'product-b&p',
  // Permitting & Licensing (formerly Citizen Services)
  // OR
  // Workflow Management
  cit = 'product-cit',
  // Reporting & Transparency
  reporting = 'product-r&t',
  // Community Feedback
  oth = 'product-oth',
  // Asset Management
  eam = 'product-eam',
  od = 'product-od',
  // Tax Collection
  tnr = 'product-tax',
  // Utility Billing
  ub = 'product-ub',
  // App Builder
  gab = 'product-gab',
  // Agent Studio
  agentstudio = 'product-agent-studio',
  // Developer Portal
  dp = 'product-developer-portal',
  // Performance Management
  perfmgmt = 'product-performance-management',
  // Workflow Builder
  wfb = 'product-wfb',
  // 311 Request Management
  product311 = 'product-311'
}

export type PRODUCT_KEY = keyof typeof PRODUCT_ICONS | keyof typeof MUI_PRODUCT_ICONS;

export type FEDERATED_SERVICE_ID_TYPE =
  | 'BY_CKAN_CUSTOMER_ID'
  | 'BY_OTH_PORTAL_ID'
  | 'BY_FINANCIALS_CUSTOMER_ID'
  | 'BY_CIT_COMMUNITY_ID'
  | 'BY_PROCUREMENT_ID'
  | 'BY_ASSET_MANAGEMENT_ID'
  | 'BY_FRIENDLY_ID';

export type FederatedServiceIds = {
  citCommunityId?: string | null;
  financialsCustomerId?: string | null;
  othPortalId?: string | null;
  ckanCustomerId?: string | null;
  procurementId?: string | null;
  assetManagementId?: string | null;
};

export interface IEntityGraphQlResponse {
  data: {
    entityByFederatedServiceId: IGraphQlResponseEntity;
  };
}

export interface IEntitledEntityAddress {
  region: string;
  city: string;
}

export interface IEntitledEntity extends IGraphQlResponseEntity {
  entityAppEntitlements: {
    entitlements: IProductDetails[];
    entityUuid: string;
  };
}
export interface IEntitledEntitiesGraphQlResponse {
  data: {
    accessibleEntities: IEntitledEntity[];
  };
  errors?: any[];
}

export type IAccessibleEntitiesResponseData = IEntitledEntitiesGraphQlResponse['data']['accessibleEntities'];
